import React, {useLayoutEffect, useMemo, useRef, useState} from 'react';
import {Tooltip} from '@mui/material';
import {Box} from "@mui/system";
import {Typography} from "@mui/joy";
import {useQuery} from "@apollo/client";
import _ from "lodash";
import {InstalledBaseNodePath} from "../../graphql/queries/installedBaseNodePath";
import {getVisibleStartIndex} from "../../utils/collapseLeadingCrumbs";

interface PathNode {
    id: string;
    title: string;
}

interface Props {
    nodeId?: string;
    /** Drop the node itself from the path and only show its ancestors. */
    excludeSelf?: boolean;
    sx?: object;
}

// Uses horizontal padding instead of margin so the separator's full footprint is
// captured by offsetWidth during measurement.
const separatorSx = {px: '8px', color: '#666', flexShrink: 0};

/**
 * Read-only breadcrumb of an installed base node's ancestor path, inclusive of the
 * node itself (e.g. "ACME GmbH / Munich / Plant 2") unless `excludeSelf` is set.
 * Reuses the asset-dashboard breadcrumb styling but without the Home icon / "Assets"
 * crumb and without links — it starts at the company/client level. Rendered as a
 * full-width bordered band (top + bottom) that bleeds past the modal's 16px content
 * padding. Renders nothing until the path loaded.
 *
 * Always a single line: when the path doesn't fit, whole leading elements are
 * replaced by a "… / " prefix (the last element is always kept) and the full path
 * is available as a tooltip on hover. Crumb widths are taken from a hidden
 * measurement row; a ResizeObserver keeps the visible count in sync with the
 * container width.
 */
const InstalledBaseNodePathBreadcrumb = ({nodeId, excludeSelf, sx}: Props) => {
    const {data} = useQuery(InstalledBaseNodePath, {
        variables: {id: nodeId!},
        skip: !nodeId,
    });

    const node = _.get(data, 'installedBaseNode');

    const crumbs: PathNode[] = useMemo(() => {
        if (!node) return [];
        const pathNodes: PathNode[] = _.get(node, 'pathNodes', []) ?? [];
        const all = pathNodes.length ? pathNodes : [{id: node.id, title: node.title}];
        return excludeSelf ? all.slice(0, -1) : all;
    }, [node, excludeSelf]);

    const containerRef = useRef<HTMLDivElement | null>(null);
    const crumbRefs = useRef<(HTMLElement | null)[]>([]);
    const ellipsisRef = useRef<HTMLElement | null>(null);
    const separatorRef = useRef<HTMLElement | null>(null);
    const [visibleStartIndex, setVisibleStartIndex] = useState(0);

    const crumbsKey = crumbs.map((crumb) => `${crumb.id}:${crumb.title}`).join('|');

    useLayoutEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const recompute = () => {
            const available = container.clientWidth;
            const separatorWidth = separatorRef.current?.offsetWidth ?? 0;
            const widths = crumbs.map((crumb, index) => crumbRefs.current[index]?.offsetWidth ?? 0);
            const ellipsisWidth = ellipsisRef.current?.offsetWidth ?? 0;
            setVisibleStartIndex(getVisibleStartIndex(widths, separatorWidth, ellipsisWidth, available));
        };

        recompute();
        const observer = new ResizeObserver(recompute);
        observer.observe(container);
        return () => observer.disconnect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [crumbsKey]);

    if (!node || !crumbs.length) return null;

    const truncated = visibleStartIndex > 0;
    const visibleCrumbs = crumbs.slice(visibleStartIndex);
    const fullPath = crumbs.map((crumb) => crumb.title).join(' / ');

    return (
        <Box
            sx={{
                // Bleed past the modal's 16px content padding so the borders span the
                // full modal width, then restore the inner alignment with matching px.
                mx: '-16px',
                px: '16px',
                py: '10px',
                borderTop: '1px solid',
                borderBottom: '1px solid',
                borderColor: 'divider',
                ...sx,
            }}
        >
            <Tooltip title={truncated ? fullPath : ''} placement={'bottom-start'}>
                <Box
                    ref={containerRef}
                    aria-label={'breadcrumb'}
                    sx={{
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        minWidth: 0,
                    }}
                >
                    {truncated && (
                        <>
                            <Typography level={'body-sm'} sx={{color: '#666', flexShrink: 0}}>…</Typography>
                            <Typography level={'body-sm'} sx={separatorSx}>/</Typography>
                        </>
                    )}
                    {visibleCrumbs.map((crumb, index) => {
                        const isLast = index === visibleCrumbs.length - 1;
                        return (
                            <React.Fragment key={crumb.id}>
                                {index > 0 && <Typography level={'body-sm'} sx={separatorSx}>/</Typography>}
                                <Typography
                                    level={'body-sm'}
                                    sx={isLast
                                        // minWidth/ellipsis so a single overlong crumb still
                                        // stays on one line.
                                        ? {color: '#000', fontWeight: 'bold', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis'}
                                        : {color: '#666', flexShrink: 0}}
                                >
                                    {crumb.title}
                                </Typography>
                            </React.Fragment>
                        );
                    })}
                    {/* Hidden measurement row: all crumbs at natural width. */}
                    <Box
                        aria-hidden
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            visibility: 'hidden',
                            display: 'flex',
                            alignItems: 'center',
                            whiteSpace: 'nowrap',
                            pointerEvents: 'none',
                        }}
                    >
                        <Typography level={'body-sm'} ref={(el) => { ellipsisRef.current = el; }}>…</Typography>
                        <Typography level={'body-sm'} sx={separatorSx} ref={(el) => { separatorRef.current = el; }}>/</Typography>
                        {crumbs.map((crumb, index) => (
                            <Typography
                                key={crumb.id}
                                level={'body-sm'}
                                sx={index === crumbs.length - 1 ? {fontWeight: 'bold'} : undefined}
                                ref={(el) => { crumbRefs.current[index] = el; }}
                            >
                                {crumb.title}
                            </Typography>
                        ))}
                    </Box>
                </Box>
            </Tooltip>
        </Box>
    );
};

export default InstalledBaseNodePathBreadcrumb;
